// =============================================================================
// authorPhotos — prywatna biblioteka zdjęć autora per mapa (Supabase Storage).
// Bucket 'author-photos', ścieżka "{mapId}/{uuid}-{nazwa}". Upload / list / download / remove.
// NIE publikowane na WP — to biblioteka do zakładania profili social.
// =============================================================================

import { supabase } from "@/integrations/supabase/client";
import { logError } from "@/services/contentEngine/contentLogger";
import type { AuthorPhoto } from "@/types/uniquePages";

const BUCKET = "author-photos";

function safeName(name: string): string {
  const dot = name.lastIndexOf(".");
  const base = (dot > 0 ? name.slice(0, dot) : name).replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 40);
  const ext = dot > 0 ? name.slice(dot).toLowerCase().replace(/[^a-z0-9.]/g, "") : "";
  return `${base || "foto"}${ext}`;
}

function publicUrl(path: string): string {
  return (supabase as any).storage.from(BUCKET).getPublicUrl(path).data.publicUrl as string;
}

export async function uploadAuthorPhoto(
  mapId: string,
  file: File,
): Promise<{ ok: boolean; photo?: AuthorPhoto; error?: string }> {
  try {
    const path = `${mapId}/${crypto.randomUUID()}-${safeName(file.name)}`;
    const { error } = await (supabase as any).storage
      .from(BUCKET)
      .upload(path, file, { upsert: false, contentType: file.type || "image/jpeg" });
    if (error) return { ok: false, error: error.message };
    return { ok: true, photo: { path, name: path.split("/").pop() || path, url: publicUrl(path) } };
  } catch (e) {
    logError("AUTHOR-PHOTOS", "upload error", e);
    return { ok: false, error: e instanceof Error ? e.message : "błąd uploadu" };
  }
}

export async function listAuthorPhotos(mapId: string): Promise<AuthorPhoto[]> {
  try {
    const { data, error } = await (supabase as any).storage
      .from(BUCKET)
      .list(mapId, { limit: 100, sortBy: { column: "created_at", order: "desc" } });
    if (error || !data) return [];
    return data
      .filter((o: any) => o.id || o.name) // pomiń placeholdery
      .map((o: any) => {
        const path = `${mapId}/${o.name}`;
        return { path, name: o.name, url: publicUrl(path), createdAt: o.created_at };
      });
  } catch (e) {
    logError("AUTHOR-PHOTOS", "list error", e);
    return [];
  }
}

export async function removeAuthorPhoto(path: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const { error } = await (supabase as any).storage.from(BUCKET).remove([path]);
    if (error) return { ok: false, error: error.message };
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : "błąd usuwania" };
  }
}

/** Pobranie pliku (blob) — wymusza download zamiast otwarcia w karcie. */
export async function downloadAuthorPhoto(photo: AuthorPhoto): Promise<void> {
  const res = await fetch(photo.url);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = photo.name;
  a.click();
  URL.revokeObjectURL(url);
}
